'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@nwwp/ui-shadcn'
import { funnelSchema } from '~/lib/schemas/funnel-schema'
import { useVerifyPhone } from '../../_hooks/use-verify-phone'
import type { FunnelStepComponent } from '../../types'
import { FunnelStep } from '../FunnelStep'

const schema = funnelSchema.pick({ verified_otp: true }).extend({ verified_otp: z.literal(true) })

const formatCountdown = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`

export const VerifyPhone: FunnelStepComponent = ({ onSubmit }) => {
  const { onOtpComplete, otpStatus, canResend, resendOtp, resendCountdown } = useVerifyPhone()
  const [otpValue, setOtpValue] = useState('')
  const { handleSubmit, setValue } = useForm({
    resolver: zodResolver(schema.extend({ otp: z.string().optional() })),
    defaultValues: { otp: '' },
    mode: 'onChange',
  })
  const formRef = useRef<HTMLFormElement>(null) as React.RefObject<HTMLFormElement>

  useEffect(() => {
    if (/^\d{6}$/.test(otpValue) && otpStatus !== 'loading' && otpStatus !== 'validated') void onOtpComplete(otpValue)
  }, [otpValue, otpStatus, onOtpComplete])

  useLayoutEffect(() => {
    if (otpStatus === 'validated') {
      setValue('verified_otp', true)
      formRef.current?.requestSubmit()
    }

    if (otpStatus !== 'invalid') return

    const timeout = setTimeout(() => setOtpValue(''), 0)
    return () => clearTimeout(timeout)
  }, [otpStatus, setValue])

  return (
    <FunnelStep onSubmit={handleSubmit(onSubmit)} formRef={formRef}>
      <FunnelStep.Title>Please enter your 6-digit security code</FunnelStep.Title>
      <div className="flex flex-col gap-3 tablet:flex-row tablet:items-center">
        <InputOTP
          maxLength={6}
          value={otpValue}
          onChange={(value) => /^\d*$/.test(value) && setOtpValue(value)}
          disabled={otpStatus === 'loading'}
          autoFocus
        >
          <InputOTPGroup>
            {Array.from({ length: 6 }).map((_, index) => (
              <InputOTPSlot key={index} index={index} />
            ))}
          </InputOTPGroup>
        </InputOTP>
        {otpStatus === 'loading' && <p className="text-body-sm">Verifying...</p>}
      </div>
      {resendCountdown !== null && (
        <p className="text-body-sm text-white">
          Didn&rsquo;t receive your code?{' '}
          {canResend ? (
            <button type="button" onClick={resendOtp} className="underline">
              Resend
            </button>
          ) : (
            <>Resend in {formatCountdown(resendCountdown)}</>
          )}
        </p>
      )}
    </FunnelStep>
  )
}

VerifyPhone.path = 'verify-phone'
VerifyPhone.schema = schema
